import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, RotateCcw } from 'lucide-react';
const WHATSAPP_NUMBER = '59898838657';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/b2cVf1TeGvzHLxtzVm3uiC/Funnel_(5).png';
const buildWaLink = (msg: string) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
type ChatMessage = {
  id: number;
  from: 'bot' | 'user';
  text: React.ReactNode;
  rawText?: string;
};
type QuickAction = {
  id: string;
  label: string;
  question: string;
  answer: string;
};
type BusinessAction = {
  id: string;
  label: string;
  emoji: string;
  waMessage: string;
};
const faqActions: QuickAction[] = [{
  id: 'precio',
  label: '¿Cuánto cuesta una web?',
  question: 'Quiero saber los precios.',
  answer: 'El plan completo cuesta USD 390 (pago único) e incluye diseño profesional, dominio por 1 año, SSL, optimización móvil, WhatsApp integrado, SEO básico, formulario de contacto y 1 año de mantenimiento + asesoramiento gratuito. ¡No hay cuotas mensuales!'
}, {
  id: 'tiempo',
  label: '¿En cuánto tiempo la entregan?',
  question: 'Quiero saber los tiempos de entrega.',
  answer: 'Entregamos tu web en máximo 72hs hábiles desde el pago. Solo necesitamos: logo, textos básicos y fotos del negocio. Si querés podemos coordinar todo por WhatsApp en menos de 10 minutos para arrancar hoy mismo.'
}, {
  id: 'incluye',
  label: '¿Qué incluye el servicio?',
  question: 'Quiero saber qué incluye el servicio.',
  answer: 'Incluye: diseño adaptado a tu rubro, mobile first, formulario activo, botón WhatsApp flotante, dominio incluido por 1 año, SSL (candado de seguridad), carga menor a 3 seg, textos personalizados con datos reales del negocio y 1 año de mantenimiento + asesoramiento sin costo extra.'
}];
const businessActions: BusinessAction[] = [{
  id: 'clinica',
  label: 'Clínica / Consultorio',
  emoji: '🏥',
  waMessage: '¡Hola! Tengo una clínica/consultorio y necesito una web premium para generar más confianza y captar turnos online. ¿Me asesoran?'
}, {
  id: 'restaurante',
  label: 'Restaurante / Bar',
  emoji: '🍽️',
  waMessage: '¡Hola! Tengo un restaurante/bar y quiero una web premium con menú digital y reservas online. ¿Me asesoran?'
}, {
  id: 'retail',
  label: 'Tienda / Retail',
  emoji: '🛍️',
  waMessage: '¡Hola! Tengo un local/tienda y quiero una web premium para mostrar mis productos y generar más ventas. ¿Me asesoran?'
}, {
  id: 'hotel',
  label: 'Hotel / Turismo',
  emoji: '🏨',
  waMessage: '¡Hola! Tengo un hotel/negocio turístico y quiero una web premium con motor de reservas directas. ¿Me asesoran?'
}, {
  id: 'servicios',
  label: 'Servicios profesionales',
  emoji: '💼',
  waMessage: '¡Hola! Brindo servicios profesionales y quiero una web premium para captar más clientes. ¿Me asesoran?'
}, {
  id: 'otro',
  label: 'Otro tipo de negocio',
  emoji: '✨',
  waMessage: '¡Hola! Tengo un negocio y quiero una web premium con WebsiteIA. ¿Me asesoran?'
}];
let mid = 0;
const buildWelcomeMessages = (): ChatMessage[] => [{
  id: ++mid,
  from: 'bot',
  text: <>
        ¡Qué lindo verte por acá! 💜 Soy el asistente de{' '}
        <span className="font-semibold text-gradient bg-gradient-to-r from-electric to-cyan" data-id="element-35">
          WebsiteIA
        </span>
        , y mi misión es ayudarte a que tu negocio se vea increíble online.
      </>
}, {
  id: ++mid,
  from: 'bot',
  text: '¿Por dónde querés empezar? Estoy para escucharte y resolverte todas las dudas que tengas ✨'
}];
function BotAvatar({
  size = 'md'
}: {
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClass = size === 'sm' ? 'w-8 h-8' : size === 'lg' ? 'w-10 h-10' : 'w-9 h-9';
  return <div className={`${sizeClass} rounded-full overflow-hidden shrink-0 bg-black border border-white/10 shadow-[0_0_15px_rgba(168,85,247,0.4)]`} data-id="element-36">
      <img src={LOGO_URL} alt="WebsiteIA" className="w-full h-full object-contain" data-id="element-37" />
    </div>;
}
export function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [askedIds, setAskedIds] = useState<string[]>([]);
  const [businessAsked, setBusinessAsked] = useState(false);
  const [botTyping, setBotTyping] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Always reset + show welcome message on each open
  useEffect(() => {
    if (open) {
      setShowTeaser(false);
      setMessages(buildWelcomeMessages());
      setAskedIds([]);
      setBusinessAsked(false);
    }
  }, [open]);
  // Teaser bubble shortly after entering (waits for intro animation to finish)
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setShowTeaser(true), 3000);
    return () => clearTimeout(timer);
  }, [open]);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, botTyping]);
  function handleAskFaq(action: QuickAction) {
    if (askedIds.includes(action.id)) return;
    setAskedIds(prev => [...prev, action.id]);
    setMessages(prev => [...prev, {
      id: ++mid,
      from: 'user',
      text: action.question,
      rawText: action.question
    }]);
    setBotTyping(true);
    setTimeout(() => {
      setBotTyping(false);
      setMessages(prev => [...prev, {
        id: ++mid,
        from: 'bot',
        text: action.answer,
        rawText: action.answer
      }]);
    }, 900);
  }
  function handleBusinessPick(action: BusinessAction) {
    if (businessAsked) return;
    setBusinessAsked(true);
    setMessages(prev => [...prev, {
      id: ++mid,
      from: 'user',
      text: `${action.emoji} ${action.label}`,
      rawText: action.label
    }]);
    setBotTyping(true);
    setTimeout(() => {
      setBotTyping(false);
      setMessages(prev => [...prev, {
        id: ++mid,
        from: 'bot',
        text: '¡Perfecto! Tenemos experiencia diseñando webs premium para tu rubro. Te paso a WhatsApp ahora mismo para asesorarte con un caso a medida 👇'
      }]);
      setTimeout(() => {
        window.open(buildWaLink(action.waMessage), '_blank');
      }, 1500);
    }, 900);
  }
  function handleReset() {
    setMessages(buildWelcomeMessages());
    setAskedIds([]);
    setBusinessAsked(false);
  }
  // Contextual WA message
  const waMessage = (() => {
    const greet = '¡Hola! Vengo desde la web de WebsiteIA.';
    if (askedIds.length === 0) {
      return `${greet} Quiero más información para activar mi web premium.`;
    }
    const askedLabels = askedIds.map(id => faqActions.find(a => a.id === id)?.label).filter(Boolean).join(' / ');
    return `${greet} En el chat consulté sobre: ${askedLabels}. Me gustaría avanzar y que me asesoren para activar mi web.`;
  })();
  const remainingFaq = faqActions.filter(a => !askedIds.includes(a.id));
  const allFaqAnswered = remainingFaq.length === 0 && askedIds.length > 0;
  return <>
      {/* Teaser bubble */}
      <AnimatePresence data-id="element-38">
        {showTeaser && !open && <motion.button onClick={() => {
        setShowTeaser(false);
        setOpen(true);
      }} initial={{
        opacity: 0,
        x: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: 20,
        scale: 0.9
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed bottom-24 right-6 z-50 max-w-[260px] bg-neutral-950/95 backdrop-blur-xl border border-white/10 rounded-2xl rounded-br-sm p-4 shadow-[0_10px_40px_rgba(0,0,0,0.6)] text-left" data-id="element-39">
            <button onClick={e => {
          e.stopPropagation();
          setShowTeaser(false);
        }} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-neutral-800 border border-white/10 flex items-center justify-center text-neutral-400 hover:text-white" aria-label="Cerrar" data-id="element-40">
              <X size={12} data-id="element-41" />
            </button>
            <div className="flex items-start space-x-2" data-id="element-42">
              <BotAvatar size="sm" data-id="element-43" />
              <div data-id="element-44">
                <div className="text-xs font-semibold text-white mb-1" data-id="element-45">
                  Asistente WebsiteIA
                </div>
                <p className="text-xs text-neutral-300 leading-relaxed" data-id="element-46">
                  ¿Te ayudo con tu web premium?
                  <br data-id="element-47" />
                  Te asesoro al instante 👋
                </p>
              </div>
            </div>
          </motion.button>}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button onClick={() => setOpen(v => !v)} initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      delay: 1.5,
      type: 'spring',
      stiffness: 200,
      damping: 15
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-electric to-neon text-white flex items-center justify-center shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_45px_rgba(168,85,247,0.7)] transition-shadow" aria-label="Abrir chat" data-id="element-48">
        <AnimatePresence mode="wait" data-id="element-49">
          {open ? <motion.div key="x" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} data-id="element-50">
              <X size={24} data-id="element-51" />
            </motion.div> : <motion.div key="msg" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} data-id="element-52">
              <MessageCircle size={24} data-id="element-53" />
            </motion.div>}
        </AnimatePresence>
        {!open && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-400 border-2 border-background animate-pulse" data-id="element-54"></span>}
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence data-id="element-55">
        {open && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed bottom-24 right-6 z-50 w-[calc(100%-3rem)] sm:w-[400px] h-[calc(100vh-12rem)] sm:h-[600px] max-h-[65vh] sm:max-h-[80vh] bg-neutral-950/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col" data-id="element-56">
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-electric/10 to-neon/10 flex items-center justify-between shrink-0" data-id="element-57">
              <div className="flex items-center space-x-3" data-id="element-58">
                <div className="relative" data-id="element-59">
                  <BotAvatar size="lg" data-id="element-60" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-neutral-950" data-id="element-61"></span>
                </div>
                <div data-id="element-62">
                  <div className="text-white font-semibold text-sm" data-id="element-63">
                    Asistente WebsiteIA
                  </div>
                  <div className="text-[11px] text-green-400 flex items-center" data-id="element-64">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5 animate-pulse" data-id="element-65"></span>
                    En línea · responde al instante
                  </div>
                </div>
              </div>
              {(askedIds.length > 0 || businessAsked) && <button onClick={handleReset} className="p-2 rounded-lg hover:bg-white/5 text-neutral-400 hover:text-white transition-colors" aria-label="Reiniciar chat" title="Reiniciar" data-id="element-66">
                  <RotateCcw size={14} data-id="element-67" />
                </button>}
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-id="element-68">
              {messages.map(msg => <motion.div key={msg.id} initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} className={`flex items-start space-x-2 ${msg.from === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`} data-id="element-69">
                  {msg.from === 'bot' ? <BotAvatar size="sm" data-id="element-70" /> : <div className="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-white text-xs font-bold shrink-0" data-id="element-71">
                      Tú
                    </div>}
                  <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${msg.from === 'bot' ? 'bg-white/5 border border-white/10 text-white rounded-2xl rounded-tl-sm' : 'bg-gradient-to-br from-electric to-neon text-white rounded-2xl rounded-tr-sm shadow-lg'}`} data-id="element-72">
                    {msg.text}
                  </div>
                </motion.div>)}

              {botTyping && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-start space-x-2" data-id="element-73">
                  <BotAvatar size="sm" data-id="element-74" />
                  <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center space-x-1" data-id="element-75">
                    <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-76"></span>
                    <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-77"></span>
                    <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-78"></span>
                  </div>
                </motion.div>}

              {/* FAQ buttons */}
              {!botTyping && remainingFaq.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="flex flex-col space-y-2 pt-3" data-id="element-79">
                  {remainingFaq.map(q => <button key={q.id} onClick={() => handleAskFaq(q)} className="group flex items-center justify-between px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-electric/40 transition-all duration-300 text-left" data-id="element-80">
                      <span className="text-sm text-white font-medium" data-id="element-81">
                        {q.label}
                      </span>
                      <Send size={13} className="text-neutral-500 group-hover:text-electric group-hover:translate-x-0.5 transition-all shrink-0 ml-2" data-id="element-82" />
                    </button>)}
                </motion.div>}

              {/* After all FAQs → invite + business type picker */}
              {!botTyping && allFaqAnswered && !businessAsked && <>
                  <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex items-start space-x-2 pt-2" data-id="element-83">
                    <BotAvatar size="sm" data-id="element-84" />
                    <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[80%]" data-id="element-85">
                      <p className="text-sm text-white leading-relaxed" data-id="element-86">
                        Para asesorarte mejor, contame: ¿qué tipo de negocio
                        tenés? 👇
                      </p>
                    </div>
                  </motion.div>
                  <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="grid grid-cols-2 gap-2 pt-2" data-id="element-87">
                    {businessActions.map(b => <button key={b.id} onClick={() => handleBusinessPick(b)} className="group flex flex-col items-start px-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-electric/40 transition-all duration-300 text-left" data-id="element-88">
                        <span className="text-lg mb-1" data-id="element-89">{b.emoji}</span>
                        <span className="text-xs text-white font-medium leading-tight" data-id="element-90">
                          {b.label}
                        </span>
                      </button>)}
                  </motion.div>
                </>}
            </div>

            {/* WhatsApp CTA */}
            <a href={buildWaLink(waMessage)} target="_blank" rel="noreferrer" className="block px-5 py-4 border-t border-white/10 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-center text-sm font-semibold transition-colors shrink-0" data-id="element-91">
              {askedIds.length > 0 ? 'Continuar por WhatsApp →' : 'Hablar por WhatsApp ahora →'}
            </a>
          </motion.div>}
      </AnimatePresence>
    </>;
}